import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * Top of the header's natural (non-sticky) position, in scroll-container
   * coordinates. See #updateScrollState.
   * @type {number}
   */
  #naturalTop = 0;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.#setState('stickyState', isIntersecting ? 'inactive' : 'active');
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        // 'idle' means "hidden because it scrolled away" (opacity 0, sticky
        // released). If the observer can see the header, that state is wrong
        // and the user is looking at a blank header-sized box. Reset it here
        // rather than waiting for a scroll event that may never be processed:
        // an in-view idle header used to set #offscreen = false, and
        // #updateScrollState returns early on !#offscreen, so once you were in
        // that box no amount of scrolling could get you out of it.
        if (isIntersecting && this.dataset.stickyState === 'idle') {
          this.#setState('stickyState', 'inactive');
          this.#setState('scrollDirection', 'none');
        }
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  /**
   * Writes a data attribute only when the value actually changes.
   *
   * `element.dataset.x = 'same-value'` still emits a MutationRecord — the
   * DOM spec fires an attribute mutation on every set, not only on change.
   * #updateScrollState runs once per animation frame while scrolling and
   * used to write both attributes unconditionally, so the header emitted
   * ~120 attribute mutations per second of scrolling even when none of its
   * state had actually changed.
   *
   * That is nearly free on a clean profile. It is not free with extensions
   * installed: password managers and coupon extensions (LastPass, Honey)
   * register broad MutationObservers over the document, so every write
   * costs a callback plus a DOM scan in each of them. That saturates the
   * main thread, and because desktop scrolls `.page-wrapper` rather than
   * the document root, a stalled main thread shows up as the scrollbar
   * moving while the content stays frozen, then jumping to catch up.
   *
   * Guarding the writes takes the steady-state mutation rate to zero.
   *
   * @param {'scrollDirection' | 'stickyState'} key
   * @param {string} value
   */
  #setState = (key, value) => {
    if (this.dataset[key] === value) return;
    this.dataset[key] = value;
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();

    // A scroll event with no change in position carries no direction. The
    // browser emits these on its own -- on tab re-activation, when the scroll
    // container's content height changes, and from the theme's own frame-by-frame
    // scroll restoration in scroll-container.js -- and the old code read every
    // one of them as "scrolling down" and hid the header.
    if (scrollTop === this.#lastScrollTop) return;

    const { top: headerTop, height: headerHeight } = this.getBoundingClientRect();
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    // Where the header sits when it is NOT stuck, in scroll-container coordinates.
    // Measured whenever it isn't sticky (the rect is its natural position then), so
    // that while it IS stuck we can still tell whether releasing it would leave any
    // of it inside the viewport. Going 'idle' (opacity 0, sticky released) while
    // that is true is what produces a blank header-sized box.
    if (this.dataset.stickyState !== 'active') {
      this.#naturalTop = headerTop + scrollTop;
    }
    const isInView = this.#naturalTop - scrollTop + headerHeight > 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.#setState('scrollDirection', 'none');
      } else if (isScrollingUp) {
        this.#setState('scrollDirection', 'up');
      } else {
        this.#setState('scrollDirection', 'down');
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isAtTop) {
      // Header is at its natural position: it is simply visible, whichever way
      // the last event went. Previously this was only checked when scrolling
      // up, so a downward or synthetic event at the top hid the header in place.
      this.#offscreen = false;
      this.#setState('stickyState', 'inactive');
      this.#setState('scrollDirection', 'none');
    } else if (isScrollingUp) {
      // show sticky header when scrolling up
      this.#setState('stickyState', 'active');
      this.#setState('scrollDirection', 'up');
    } else if (isInView) {
      // Scrolling down, but the header's natural position is still partly on
      // screen. Keep it visible in place; it hides once it has actually gone.
      this.#setState('scrollDirection', 'none');
      this.#setState('stickyState', 'inactive');
    } else {
      // Scrolling down: release the sticky header and let it scroll away.
      this.#setState('scrollDirection', 'none');
      this.#setState('stickyState', 'idle');
    }

    this.#lastScrollTop = scrollTop;
  };


  /** @type {MutationObserver | null} */
  #pageWrapperObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #squeezeSettleTimer = null;

  /**
   * Restores the header menu after a side drawer stops squeezing the page.
   *
   * An open drawer puts a margin on `.page-wrapper`, which shrinks the header's centre
   * column and makes overflow-list park items under "More". When the drawer closes the
   * margin returns to 0, but nothing brings the menu back: overflow-list only observes
   * itself, and with items parked its own box is content-sized, so the column stays
   * narrow, no resize fires, and no reflow happens. The menu is stuck until reload.
   *
   * Toggling `disabled` is overflow-list's public lever -- setting it runs #reset()
   * (everything back to the default slot), removing it runs #reflowItems() against the
   * real width. Both attribute callbacks run synchronously, so there is no paint in
   * between and no flash of an overflowing menu.
   */
  #observeDrawerSqueeze() {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (!pageWrapper) return;

    const isSqueezed = () => /drawer-open/.test(pageWrapper.className);
    let squeezed = isSqueezed();

    this.#pageWrapperObserver = new MutationObserver(() => {
      const nowSqueezed = isSqueezed();
      const released = squeezed && !nowSqueezed;
      squeezed = nowSqueezed;
      if (!released) return;

      // The class drops while the margin is still animating out, so wait for the
      // margin to finish before measuring -- reflowing mid-animation measures an
      // intermediate width and leaves the menu short.
      const settle = () => {
        if (this.#squeezeSettleTimer) {
          clearTimeout(this.#squeezeSettleTimer);
          this.#squeezeSettleTimer = null;
        }
        pageWrapper.removeEventListener('transitionend', onTransitionEnd);
        if (isSqueezed()) return;

        const overflowList = this.querySelector('overflow-list');
        if (!overflowList) return;
        overflowList.setAttribute('disabled', 'true');
        overflowList.removeAttribute('disabled');
      };

      /** @param {TransitionEvent} event */
      const onTransitionEnd = (event) => {
        if (event.target === pageWrapper && event.propertyName.startsWith('margin')) settle();
      };

      pageWrapper.addEventListener('transitionend', onTransitionEnd);
      // Fallback for when the margin is removed without a transition.
      this.#squeezeSettleTimer = setTimeout(settle, 500);
    });

    this.#pageWrapperObserver.observe(pageWrapper, { attributes: true, attributeFilter: ['class'] });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#observeDrawerSqueeze();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#pageWrapperObserver?.disconnect();
    this.#pageWrapperObserver = null;
    if (this.#squeezeSettleTimer) {
      clearTimeout(this.#squeezeSettleTimer);
      this.#squeezeSettleTimer = null;
    }
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    // Only zero this when no header remains in the document.
    //
    // On a node replace the incoming header connects (and its ResizeObserver
    // sets a correct height) BEFORE the outgoing one disconnects, so an
    // unconditional reset here overwrites a good value with 0px. Nothing on the
    // storefront recomputes it afterwards — updateAllHeaderCustomProperties() in
    // utilities.js is imported by theme-editor.js only — so the collapsed value
    // sticks for the rest of the session, and a transparent header then renders
    // its white text over the page background instead of over the hero.
    if (!document.querySelector('header-component')) {
      document.body.style.setProperty('--header-height', '0px');
    }
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
